import { useTranslation } from 'react-i18next';
import { colors } from '../../assets/colors';
import { Text } from '../../helpers';
import { IInvoice } from '../../interfaces';
import UserInfoSchema from '../../schemas/UserInfoSchema';
import { setGlobalState } from '../../state';
import AnimatedLoader from '../AnimatedLoader/AnimatedLoader';
import CustomForm from '../CustomForm/CustomForm';
import RoundBox from '../RoundBox/RoundBox';
import PaymentProviderFooter from './PaymentProviderFooter';
import { approveCopy, resumeStripePayment } from '../../functions/approveCopy';
import { formFieldsConverter } from '../../functions';
import Button from '../Button/Button';
import signInvoice, { buildSignInvoicePayload } from '../../api/signInvoice/signInvoice';

type EditUserInfoProps = {
    editting?: boolean;
    invoice: IInvoice;
    loading: boolean;
};

const EditUserInfo = ({ invoice, loading }: EditUserInfoProps) => {
    const { t } = useTranslation();
    const copy = approveCopy(invoice.payment_provider);
    const resumePayment = resumeStripePayment(invoice);
    const initialValues: Partial<IInvoice['customer']> = {
        email: invoice.customer.email,
        name: invoice.customer.name,
        street_address: invoice.customer.street_address,
        care_of: invoice.customer.care_of,
        postal_code: invoice.customer.postal_code,
        city: invoice.customer.city,
        phone_number: invoice.customer.phone_number
    };

    const submitUserInfoForm = async (values: Partial<IInvoice['customer']>) => {
        setGlobalState('loading', true);
        if (loading) {
            return;
        }
        await signInvoice(
            invoice.uuid,
            buildSignInvoicePayload(invoice, {
                overrides: values,
                ...(resumePayment ? { needBankSignature: false } : {})
            }),
            invoice.payment_provider
        );
        setGlobalState('loading', false);
    };

    const EditUserInfoFields = formFieldsConverter(Object.keys(invoice.customer));

    return (
        <RoundBox>
            {loading && <AnimatedLoader />}
            <Text
                i="Fakturauppgifter"
                fontWeight="normal"
                style={{ fontSize: '18px', marginBottom: '13px' }}
            />
            {invoice.customer.type !== 'B2C' && (
                <>
                    <Text
                        i="Organisationsnummer"
                        color={colors.lightGrey}
                        style={{ marginTop: '13px', marginBottom: '13px' }}
                    />
                    <Text
                        i={invoice.organization.id_number}
                        color="#2A2E43"
                        style={{ marginBottom: '29px' }}
                    />
                </>
            )}

            <CustomForm
                initialValues={initialValues}
                validationSchema={UserInfoSchema}
                onFormSubmit={submitUserInfoForm}
                fields={EditUserInfoFields}
                buttonText={
                    resumePayment ? t('continueToPayment') : t(copy.withBankID ?? 'complete')
                }
                showBankIdLogo={!resumePayment}
            />
            {!invoice.is_signing_required && !resumePayment && (
                <Button
                    i={t(copy.withoutBankID)}
                    color={colors.white}
                    fontWeight="bold"
                    style={{
                        marginTop: '8%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px'
                    }}
                    disabled={loading}
                    onClick={async () => {
                        setGlobalState('loading', true);
                        await signInvoice(
                            invoice.uuid,
                            buildSignInvoicePayload(invoice, { needBankSignature: false }),
                            invoice.payment_provider
                        );
                        setGlobalState('loading', false);
                    }}
                />
            )}

            <PaymentProviderFooter invoice={invoice} />
        </RoundBox>
    );
};

export default EditUserInfo;
